import { getScreenTranslations, createTranslation } from "../../Api/content"

// Caché en memoria para las traducciones
const translationsCache = new Map();
// Caché para las llamadas en curso (evita llamadas duplicadas)
const pendingRequests = new Map();

// Clave para localStorage
const CACHE_KEY = 'translations_cache';
const CACHE_VERSION = '1.0';
const CACHE_EXPIRY_HOURS = 24; // El caché expira después de 24 horas

/**
 * Carga el caché desde localStorage
 */
function loadCacheFromStorage() {
  try {
    const cached = localStorage.getItem(CACHE_KEY);
    if (!cached) return null;
    
    const parsed = JSON.parse(cached);
    
    // Verificar versión y expiración
    if (parsed.version !== CACHE_VERSION) return null;
    
    const now = Date.now();
    if (now - parsed.timestamp > CACHE_EXPIRY_HOURS * 60 * 60 * 1000) {
      localStorage.removeItem(CACHE_KEY);
      return null;
    }
    
    return parsed.data;
  } catch (error) {
    console.error('Error loading cache from storage:', error);
    return null;
  }
}

/**
 * Guarda el caché en localStorage
 */
function saveCacheToStorage(data) {
  try {
    const cacheData = {
      version: CACHE_VERSION,
      timestamp: Date.now(),
      data: data
    };
    localStorage.setItem(CACHE_KEY, JSON.stringify(cacheData));
  } catch (error) {
    console.error('Error saving cache to storage:', error);
  }
}

/**
 * Obtiene todas las traducciones con caché
 */
async function getCachedTranslations() {
  const cacheKey = 'all_translations';
  
  // Verificar caché en memoria
  if (translationsCache.has(cacheKey)) {
    return translationsCache.get(cacheKey);
  }
  
  // Verificar si hay una petición en curso
  if (pendingRequests.has(cacheKey)) {
    return pendingRequests.get(cacheKey);
  }
  
  // Verificar caché en localStorage
  const storageCache = loadCacheFromStorage();
  if (storageCache && storageCache.translations) {
    translationsCache.set(cacheKey, storageCache);
    return storageCache;
  }
  
  // Hacer la petición
  const requestPromise = getScreenTranslations()
    .then((response) => {
      if (response?.responseStatus === "Success" && response?.data) {
        const cacheData = {
          translations: response.data.translations || [],
          lastUpdate: Date.now()
        };
        
        // Guardar en caché de memoria
        translationsCache.set(cacheKey, cacheData);
        
        // Guardar en localStorage
        saveCacheToStorage(cacheData);
        
        // Limpiar de peticiones pendientes
        pendingRequests.delete(cacheKey);
        
        return cacheData;
      }
      
      pendingRequests.delete(cacheKey);
      return null;
    })
    .catch((error) => {
      pendingRequests.delete(cacheKey);
      throw error;
    });
  
  pendingRequests.set(cacheKey, requestPromise);
  return requestPromise;
}

/**
 * Invalida el caché (útil cuando se crea o actualiza una traducción)
 */
export function invalidateTranslationsCache() {
  translationsCache.clear();
  localStorage.removeItem(CACHE_KEY);
}

/**
 * Genera una key dinámica a partir de un texto
 * - Si el texto es <= 15 caracteres: convierte a formato KEY (ej: "DIPLOMADO EN TIC" -> "DIPLOMADO_EN_TIC")
 * - Si el texto es > 15 caracteres: primeros 10 caracteres + código aleatorio de 4 dígitos
 */
function generateKey(text) {
    const cleanText = text.trim().toUpperCase();
    
    if (cleanText.length <= 15) {
      // Convertir a formato KEY: reemplazar espacios y caracteres especiales con guiones bajos
      return cleanText
        .replace(/[^A-Z0-9]/g, '_')
        .replace(/_+/g, '_')
        .replace(/^_|_$/g, '');
    } else {
      // Primeros 10 caracteres + código aleatorio de 4 dígitos
      const first10 = cleanText
        .substring(0, 10)
        .replace(/[^A-Z0-9]/g, '_')
        .replace(/_+/g, '_')
        .replace(/^_|_$/g, '');
      const randomCode = Math.floor(1000 + Math.random() * 9000); // 4 dígitos aleatorios
      return `${first10}_${randomCode}`;
    }
  }
  
  /**
   * Función para traducir un valor basado en la pantalla y el texto original
   * @param screen - Nombre de la pantalla
   * @param text - Texto original que se usará como valor por defecto
   * @param locale - Idioma deseado ("es" o "en"), por defecto "es"
   * @returns Promise<string> - El texto traducido en el idioma solicitado, o en español como fallback
   */
  export async function _translateValue(screen, text, locale = "es") {
    if (!screen || !text) {
      return text;
    }
  
    try {
      // Obtener todas las traducciones de la pantalla (con caché)
      const cacheData = await getCachedTranslations();
      
      if (cacheData && cacheData.translations) {
        const translations = cacheData.translations;
        
        // Buscar si ya existe una traducción en español ("es") para ese texto en esa pantalla
        const existingTranslation = translations.find(
          (t) => 
            t.screen === screen && 
            t.es === text
        );
  
        if (existingTranslation) {
          // Si existe la traducción, retornar según el locale solicitado
          if (locale === "en" && existingTranslation.en) {
            // Si se solicita inglés y existe, retornar inglés
            return existingTranslation.en;
          } else {
            // Si se solicita español o no hay traducción en inglés, retornar español (fallback)
            return existingTranslation.es || text;
          }
        }
  
        // Si no existe, crear una nueva key y traducción
        // Generar key dinámica
        const key = generateKey(text);
        
        // Verificar que la key no exista ya en esa pantalla
        let keyExists = translations.some(
          (t) => t.screen === screen && t.key === key
        );
        
        // Si la key existe, agregar un sufijo numérico
        let counter = 1;
        let finalKey = key;
        while (keyExists) {
          finalKey = `${key}_${counter}`;
          keyExists = translations.some(
            (t) => t.screen === screen && t.key === finalKey
          );
          counter++;
        }
  
        // Crear la traducción con el texto en español
        try {
          await createTranslation({
            key: finalKey,
            screen: screen,
            traducciones: {
              es: text
            }
          });
          
          // Invalidar el caché después de crear una nueva traducción
          invalidateTranslationsCache();
        } catch (error) {
          console.error(`Error creating translation for key ${finalKey}:`, error);
          // Si falla la creación, retornar el texto original
          return text;
        }
  
        // Retornar el texto original (que ahora está guardado como traducción en español)
        return text;
      }
  
      // Si no hay respuesta válida, retornar el texto original
      return text;
    } catch (error) {
      console.error(`Error in _translateValue for screen ${screen}, text ${text}:`, error);
      // En caso de error, retornar el texto original
      return text;
    }
  }
  
  